import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { activate, fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from '../services/firebase';

const RemoteConfigContext = createContext(undefined);

export const RemoteConfigProvider = ({ children }) => {
    // `isAppUnavailable` começa `false`, então sem esta flag o app renderizava
    // normalmente e só trocava para a mensagem de bloqueio quando o fetch
    // voltava. Quem consome espera `isLoadingConfig` antes de decidir.
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);
    const [config, setConfig] = useState({
        hasPlanner: false,
        hasScheduleNotifications: false,
        hasGeneralNotifications: false,
        hasComments: false,
        hasProgress: false,
        hasProfileInfos: false,
        hasDownload: false,
        api_key_panda: '',
        isAppUnavailable: false,
        hasCommunity: false,
        paymentGateway: '',
        hasForm: false,
        api_key_goog: '',
        api_key_appl: '',
        hasProductCatalog: false,
    });

    useEffect(() => {
        // ATENÇÃO: atribua CAMPO A CAMPO. `settings` é um objeto simples do SDK
        // com dois campos (`fetchTimeoutMillis` e `minimumFetchIntervalMillis`);
        // trocar o objeto inteiro apagava o `fetchTimeoutMillis` (padrão 60s) e
        // o deixava `undefined`. O SDK faz
        // `setTimeout(() => abortSignal.abort(), settings.fetchTimeoutMillis)`,
        // então `undefined` virava 0ms: o abort disparava no tick seguinte ao
        // início do fetch. O próprio SDK avisa no código que "a very low delay
        // can elapse before listeners are initialized" — ou seja, se o abort
        // chega antes de o listener existir ele se perde e o fetch funciona; se
        // chega depois, o fetch morre com `remoteconfig/fetch-timeout`. Era uma
        // corrida, e ela virava perda quando o boot ficava mais concorrido.
        // Resultado: config caía nos defaults (tudo `false`) e as
        // funcionalidades sumiam do app.
        remoteConfig.settings.minimumFetchIntervalMillis = 0;
        remoteConfig.defaultConfig = {
            hasPlanner: "false",
            hasScheduleNotifications: "false",
            hasGeneralNotifications: "false",
            hasComments: "false",
            hasProgress: "false",
            hasProfileInfos: "false",
            hasDownload: "false",
            api_key_panda: "",
            isAppUnavailable: "false",
            hasCommunity: "false",
            paymentGateway: "",
            hasForm: "false",
            api_key_appl: "",
            api_key_goog: "",
            hasProductCatalog: "false",
        }
        const readConfig = () => ({
            hasPlanner: getValue(remoteConfig, "hasPlanner").asBoolean(),
            hasScheduleNotifications: getValue(remoteConfig, "hasScheduleNotifications").asBoolean(),
            hasGeneralNotifications: getValue(remoteConfig, "hasGeneralNotifications").asBoolean(),
            hasComments: getValue(remoteConfig, "hasComments").asBoolean(),
            hasProgress: getValue(remoteConfig, "hasProgress").asBoolean(),
            hasProfileInfos: getValue(remoteConfig, "hasProfileInfos").asBoolean(),
            hasDownload: getValue(remoteConfig, "hasDownload").asBoolean(),
            api_key_panda: getValue(remoteConfig, "api_key_panda").asString(),
            isAppUnavailable: getValue(remoteConfig, "isAppUnavailable").asBoolean(),
            hasCommunity: getValue(remoteConfig, "hasCommunity").asBoolean(),
            paymentGateway: getValue(remoteConfig, "paymentGateway").asString(),
            hasForm: getValue(remoteConfig, "hasForm").asBoolean(),
            api_key_appl: getValue(remoteConfig, "api_key_appl").asString(),
            api_key_goog: getValue(remoteConfig, "api_key_goog").asString(),
            hasProductCatalog: getValue(remoteConfig, "hasProductCatalog").asBoolean(),
        });

        fetchAndActivate(remoteConfig)
            .then(() => {
                setConfig(readConfig());
            })
            .catch(async (err) => {
                console.error('Error fetching remote config:', err);
                // Falhou o fetch (rede, timeout, throttle). Em vez de desligar
                // TODAS as funcionalidades, aproveita a última configuração que
                // o SDK guardou localmente. Só cai nos defaults quem nunca
                // conseguiu buscar nenhuma vez.
                try {
                    await activate(remoteConfig);
                    setConfig(readConfig());
                } catch (activateError) {
                    console.error('Error activating cached remote config:', activateError);
                }
            })
            .finally(() => {
                // Falha no fetch mantém os defaults (app disponível) em vez de
                // deixar o boot preso no loading.
                setIsLoadingConfig(false);
            });
    }, []);

    const value = useMemo(
        () => ({ ...config, isLoadingConfig }),
        [config, isLoadingConfig]
    );

    return (
        <RemoteConfigContext.Provider value={value}>
            {children}
        </RemoteConfigContext.Provider>
    );
};

export const useRemoteConfigContext = () => {
    const context = useContext(RemoteConfigContext);
    if (context === undefined) {
        throw new Error('useRemoteConfigContext must be used within a RemoteConfigProvider');
    }
    return context;
};